import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Shell } from "../components/Shell";
import { ApiError, tenants } from "../lib/api";
import type {
  AIProviderID,
  PlatformConfig,
  ProviderStatus,
  Tenant,
  TenantSoporteRag,
  TenantSoporteRagUpdate,
} from "../lib/api";

const PROVIDER_LABEL: Record<AIProviderID, string> = {
  anthropic: "Anthropic Claude",
  openai: "OpenAI ChatGPT",
  gemini: "Google Gemini",
  lm_studio: "LM Studio (local)",
  ollama: "Ollama (local)",
};

export function TenantSoporteRagPage() {
  const { id } = useParams<{ id: string }>();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [cfg, setCfg] = useState<TenantSoporteRag | null>(null);
  const [platform, setPlatform] = useState<PlatformConfig | null>(null);
  const [models, setModels] = useState<{ id: string; label: string }[]>([]);
  const [modelsBusy, setModelsBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const flash = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 2400);
  };

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([tenants.get(id), tenants.getSoporteRag(id), tenants.getPlatformConfig()])
      .then(([t, c, p]) => {
        setTenant(t);
        setCfg(c);
        setPlatform(p);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : "No se pudo cargar"))
      .finally(() => setLoading(false));
  }, [id]);

  // Cargar modelos del proveedor seleccionado
  useEffect(() => {
    if (!cfg?.provider) return;
    setModelsBusy(true);
    tenants
      .listProviderModels(cfg.provider)
      .then((r) => setModels(r.models))
      .catch(() => setModels([]))
      .finally(() => setModelsBusy(false));
  }, [cfg?.provider]);

  const update = async (patch: TenantSoporteRagUpdate) => {
    if (!id) return;
    setBusy(true);
    try {
      const c = await tenants.updateSoporteRag(id, patch);
      setCfg(c);
      flash("ok", "Guardado");
    } catch (err) {
      flash("err", err instanceof ApiError ? err.message : "Error");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <section className="page">
          <div className="eyebrow">Cargando…</div>
        </section>
      </Shell>
    );
  }

  if (error || !tenant || !cfg || !platform) {
    return (
      <Shell>
        <section className="page">
          <div className="alert err">{error ?? "Tenant no encontrado."}</div>
          <p>
            <Link to="/tenants">← Volver</Link>
          </p>
        </section>
      </Shell>
    );
  }

  // Solo proveedores configurados en /platform/config son seleccionables
  const availableProviders = platform.providers.filter((p) => p.configured);

  return (
    <Shell>
      <section className="page">
        <div className="eyebrow">
          <Link to={`/tenants/${tenant.id}`}>← {tenant.nombre}</Link> · Bot RAG del chat ciudadano
        </div>
      <h1 style={{ fontSize: 28, marginTop: 6 }}>Bot inteligente del chat ciudadano</h1>
      <p className="lede" style={{ maxWidth: 640 }}>
        Cuando un ciudadano inicia un chat, el LLM lee los artículos más relevantes del
        centro de ayuda y responde en lenguaje natural citando uno. Si ninguno aplica o
        no está seguro, escala automáticamente a un operador.
      </p>

      {availableProviders.length === 0 && (
        <div className="alert warn" style={{ marginTop: 18, maxWidth: 640 }}>
          No hay proveedores de IA configurados en la plataforma.{" "}
          <Link to="/platform/config">Configura al menos uno</Link> antes de activar el
          asistente.
        </div>
      )}

      {/* Toggle on/off */}
      <div className="card" style={{ marginTop: 24 }}>
        <div className="card-inner">
          <div className="eyebrow">Estado</div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 14,
              marginTop: 10,
              cursor: availableProviders.length === 0 ? "not-allowed" : "pointer",
              opacity: availableProviders.length === 0 ? 0.5 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={cfg.enabled}
              disabled={busy || availableProviders.length === 0}
              onChange={(e) => void update({ enabled: e.target.checked })}
              style={{ width: 20, height: 20 }}
            />
            <span style={{ fontSize: 15, color: "var(--ink)" }}>
              {cfg.enabled
                ? "Bot RAG activado para esta alcaldía"
                : "Bot RAG desactivado (chat usa solo búsqueda KB)"}
            </span>
          </label>
        </div>
      </div>

      {/* Consentimiento Habeas Data para envío de PII a proveedores cloud */}
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-inner">
          <div className="eyebrow">Habeas Data · Transferencia internacional</div>
          <p
            className="helper"
            style={{ marginTop: 8, marginBottom: 12, color: "var(--ink-2)" }}
          >
            Si el proveedor es cloud (Anthropic / OpenAI / Gemini), los
            mensajes del ciudadano salen del país. Ley 1581/2012 exige
            consentimiento explícito de la alcaldía para esto. Si no
            aceptas, el RAG queda inactivo aunque esté "activado" arriba
            — el chat seguirá funcionando con búsqueda KB únicamente.
            Los proveedores locales (LM Studio / Ollama) no tienen
            esta restricción.
          </p>
          <label
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 14,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={cfg.permite_pii_externa}
              disabled={busy}
              onChange={(e) =>
                void update({ permite_pii_externa: e.target.checked })
              }
              style={{ width: 20, height: 20, marginTop: 2 }}
            />
            <span style={{ fontSize: 14, color: "var(--ink)" }}>
              La alcaldía autoriza enviar los mensajes del ciudadano a
              un proveedor cloud para generar respuestas con IA, en los
              términos de la política de privacidad publicada.
            </span>
          </label>
        </div>
      </div>

      {/* Proveedor */}
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-inner">
          <div className="eyebrow">Proveedor de IA</div>
          <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0", display: "grid", gap: 8 }}>
            {(["anthropic", "openai", "gemini", "lm_studio", "ollama"] as AIProviderID[]).map(
              (p) => {
                const status = platform.providers.find((x) => x.provider === p) as
                  | ProviderStatus
                  | undefined;
                // El usuario puede elegir provider/modelo aunque el toggle
                // esté en OFF (para preconfigurar). Solo bloqueamos si el
                // proveedor no tiene credenciales o estamos guardando.
                const disabled = !status?.configured || busy;
                return (
                  <li key={p}>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        padding: "10px 12px",
                        border: "1px solid var(--hairline)",
                        borderRadius: 10,
                        cursor: disabled ? "not-allowed" : "pointer",
                        background:
                          cfg.provider === p ? "var(--bone-warm)" : "var(--paper)",
                        opacity: disabled ? 0.45 : 1,
                      }}
                    >
                      <input
                        type="radio"
                        name="provider"
                        value={p}
                        checked={cfg.provider === p}
                        disabled={disabled}
                        onChange={() => {
                          // Solo cambia el provider; el usuario elige el modelo
                          // del listado dinámico que se carga después. Pasar
                          // model:"" haría que el backend rechazara la request.
                          void update({ provider: p });
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 600, color: "var(--ink)" }}>
                          {PROVIDER_LABEL[p]}
                        </div>
                        <div
                          style={{
                            fontFamily: "var(--font-mono)",
                            fontSize: 11,
                            color: "var(--ink-3)",
                            marginTop: 2,
                          }}
                        >
                          {status?.configured ? "configurado" : "sin credenciales"}
                        </div>
                      </div>
                    </label>
                  </li>
                );
              },
            )}
          </ul>
        </div>
      </div>

      {/* Modelo */}
      <div className="card" style={{ marginTop: 18 }}>
        <div className="card-inner">
          <div className="eyebrow">Modelo</div>
          <p className="helper">
            {cfg.provider === "lm_studio" || cfg.provider === "ollama"
              ? "Modelos cargados en tu endpoint local."
              : "Modelos sugeridos del proveedor."}
          </p>
          {modelsBusy ? (
            <p className="helper">Cargando modelos…</p>
          ) : models.length === 0 ? (
            <input
              type="text"
              className="input"
              value={cfg.model}
              onChange={(e) => void update({ model: e.target.value })}
              placeholder="Escribe el nombre del modelo"
              disabled={busy}
            />
          ) : (
            <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0", display: "grid", gap: 8 }}>
              {models.map((m) => (
                <li key={m.id}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "10px 12px",
                      border: "1px solid var(--hairline)",
                      borderRadius: 10,
                      cursor: busy ? "not-allowed" : "pointer",
                      background: cfg.model === m.id ? "var(--bone-warm)" : "var(--paper)",
                    }}
                  >
                    <input
                      type="radio"
                      name="model"
                      value={m.id}
                      checked={cfg.model === m.id}
                      disabled={busy}
                      onChange={() => void update({ model: m.id })}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: "var(--ink)" }}>{m.label}</div>
                      <div
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 11,
                          color: "var(--ink-3)",
                          marginTop: 2,
                        }}
                      >
                        {m.id}
                      </div>
                    </div>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

        <RagTester tenantId={tenant.id} enabled={cfg.enabled} />

        {toast && <div className={`toast ${toast.kind}`} role="status" aria-live="polite">{toast.msg}</div>}
      </section>
    </Shell>
  );
}

type TestResult = Awaited<ReturnType<typeof tenants.testSoporteRag>>;

function RagTester({
  tenantId,
  enabled,
}: {
  tenantId: string;
  enabled: boolean;
}) {
  const [query, setQuery] = useState(
    "¿Cuáles son los horarios de atención de la alcaldía?",
  );
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<TestResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const r = await tenants.testSoporteRag(tenantId, query.trim());
      setResult(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ marginTop: 18 }}>
      <div className="card-inner">
        <div className="eyebrow">Probar el bot</div>
        <p className="helper" style={{ marginTop: 6 }}>
          Lanza una consulta de prueba contra la configuración actual. No se
          guarda ninguna conversación en la base de datos.
        </p>

        {!enabled && (
          <div
            className="alert warn"
            style={{ marginTop: 10 }}
          >
            Activa el bot arriba antes de probarlo.
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
          <input
            type="text"
            className="input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Escribe una consulta del ciudadano…"
            style={{ flex: 1 }}
            disabled={busy}
          />
          <button
            type="button"
            className="btn-outline"
            onClick={() => void run()}
            disabled={busy || !enabled || query.trim().length < 2}
            style={{ minWidth: 110 }}
          >
            {busy ? "Probando…" : "Probar →"}
          </button>
        </div>

        {error && (
          <div className="alert err" style={{ marginTop: 12 }}>
            {error}
          </div>
        )}

        {result && (
          <div style={{ marginTop: 14 }}>
            <div
              style={{
                display: "flex",
                gap: 12,
                fontSize: 11,
                fontFamily: "var(--font-mono)",
                color: "var(--ink-3)",
                marginBottom: 10,
              }}
            >
              <span
                style={{
                  color: result.ok ? "var(--ok)" : "var(--err)",
                  textTransform: "uppercase",
                  letterSpacing: "0.14em",
                }}
              >
                {result.ok ? "● OK" : "● FALLO"}
              </span>
              <span>KB matches: {result.kb_count}</span>
              <span>Latencia: {result.latency_ms} ms</span>
              {result.escalar !== null && (
                <span>escalar: {result.escalar ? "sí" : "no"}</span>
              )}
            </div>

            {result.error && (
              <div className="alert warn" style={{ marginBottom: 10 }}>
                {result.error}
              </div>
            )}

            {result.respuesta && (
              <div
                style={{
                  padding: 14,
                  background: "var(--bone-warm)",
                  border: "1px solid var(--hairline)",
                  borderRadius: 10,
                  fontSize: 14,
                  lineHeight: 1.5,
                  marginBottom: 10,
                  whiteSpace: "pre-wrap",
                }}
              >
                <div
                  style={{
                    fontSize: 10,
                    fontFamily: "var(--font-mono)",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "var(--ink-3)",
                    marginBottom: 6,
                  }}
                >
                  Respuesta del bot
                  {result.articulo_slug && (
                    <span style={{ color: "var(--ok)", marginLeft: 8 }}>
                      cita: {result.articulo_slug}
                    </span>
                  )}
                </div>
                {result.respuesta}
              </div>
            )}

            {result.kb_titulos.length > 0 && (
              <details style={{ fontSize: 12, color: "var(--ink-3)" }}>
                <summary style={{ cursor: "pointer" }}>
                  Artículos KB candidatos ({result.kb_titulos.length})
                </summary>
                <ul style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                  {result.kb_titulos.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
